import type {
  ClientInfo,
  AdDetectInfo,
  AdLoadedInfo,
  AdImpressionInfo,
  PlayerAnalyticsContext,
  StormcloudVideoPlayerConfig,
} from "../types";

import { buildPlayerTopic, isMQTTEnabled } from "./mqttConfig";
import { ensureMQTTClient, publishMQTT } from "./mqttClient";
import {
  getOrCreateStoredUuid,
  PLAYER_ID_STORAGE_KEY,
  resolveNativeDeviceId,
} from "./vastEnvironmentSignals";

let cachedBrowserId: string | null = null;

export function buildAnalyticsContext(
  config: Pick<StormcloudVideoPlayerConfig, "debugAdTiming" | "isLiveStream">
): PlayerAnalyticsContext {
  const context: PlayerAnalyticsContext = {};
  if (config.debugAdTiming) {
    context.debugAdTiming = true;
  }
  if (config.isLiveStream !== undefined) {
    context.inputStreamType = config.isLiveStream ? "live" : "vod";
  }
  return context;
}

export function getClientInfo(): ClientInfo {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  const vendor = navigator.vendor || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const memory = (navigator as any).deviceMemory || null;
  const hardwareConcurrency = navigator.hardwareConcurrency || 1;

  const screenInfo = {
    width: screen?.width,
    height: screen?.height,
    availWidth: screen?.availWidth,
    availHeight: screen?.availHeight,
    orientation: (screen?.orientation as any)?.type || "",
    pixelDepth: screen?.pixelDepth,
  };

  let deviceType: "tv" | "mobile" | "tablet" | "desktop" = "desktop";
  let brand = "Unknown";
  let os = "Unknown";
  let model = "";
  let isSmartTV = false;
  let isAndroid = false;
  let isWebView = false;
  let isWebApp = false;

  if (ua.includes("Web0S")) {
    brand = "LG";
    os = "webOS";
    isSmartTV = true;
    deviceType = "tv";
    const webosMatch = ua.match(/Web0S\/([^\s]+)/);
    model = webosMatch ? `webOS ${webosMatch[1]}` : "webOS TV";
  } else if (ua.includes("Tizen")) {
    brand = "Samsung";
    os = "Tizen";
    isSmartTV = true;
    deviceType = "tv";
    const tizenMatch = ua.match(/Tizen\/([^\s]+)/);
    const tvMatch = ua.match(/(?:Smart-TV|SMART-TV|TV)/i) ? "Smart TV" : "";
    model = tizenMatch
      ? `Tizen ${tizenMatch[1]} ${tvMatch}`.trim()
      : "Tizen TV";
  } else if (ua.includes("Philips")) {
    brand = "Philips";
    os = "Saphi";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("Sharp") || ua.includes("AQUOS")) {
    brand = "Sharp";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (
    ua.includes("Android") &&
    (ua.includes("Sony") || vendor.includes("Sony"))
  ) {
    brand = "Sony";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (
    ua.includes("Android") &&
    (ua.includes("NetCast") || ua.includes("LG"))
  ) {
    brand = "LG";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes(" Roku") || ua.includes("Roku/")) {
    brand = "Roku";
    os = "Roku OS";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("AppleTV")) {
    brand = "Apple";
    os = "tvOS";
    isSmartTV = true;
    deviceType = "tv";
  }

  if (ua.includes("Android")) {
    isAndroid = true;
    os = "Android";
    deviceType = /Mobile/.test(ua) ? "mobile" : "tablet";

    if (
      ua.includes("Android") &&
      (maxTouchPoints === 0 ||
        ua.includes("Google TV") ||
        ua.includes("XiaoMi"))
    ) {
      deviceType = "tv";
      isSmartTV = true;
      brand = brand === "Unknown" ? "Android TV" : brand;
    }

    const androidModelMatch = ua.match(/\(([^)]*Android[^)]*)\)/);
    if (androidModelMatch && androidModelMatch[1]) {
      model = androidModelMatch[1];
    }
  }

  if (/iPad|iPhone|iPod/.test(ua)) {
    os = "iOS";
    deviceType = "mobile";
    brand = "Apple";
    if (navigator.maxTouchPoints > 1 && /iPad/.test(ua)) {
      deviceType = "tablet";
    }
  }

  if (!isAndroid && !isSmartTV && !/Mobile/.test(ua)) {
    if (ua.includes("Windows")) {
      os = "Windows";
      deviceType = "desktop";
    } else if (ua.includes("Mac") && !/iPhone/.test(ua)) {
      os = "macOS";
      deviceType = "desktop";
      if (maxTouchPoints > 1) deviceType = "tablet";
    } else if (ua.includes("Linux")) {
      os = "Linux";
      deviceType = "desktop";
    }
  }

  if (brand === "Unknown") {
    if (vendor.includes("Google") || ua.includes("Chrome")) brand = "Google";
    if (vendor.includes("Apple")) brand = "Apple";
    if (vendor.includes("Samsung") || ua.includes("SM-")) brand = "Samsung";
  }

  isWebView = /wv|WebView|Linux; U;/.test(ua);

  if (window?.outerHeight === 0 && window?.outerWidth === 0) {
    isWebView = true;
  }

  isWebApp =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true ||
    window.screen?.orientation?.angle !== undefined;

  return {
    brand,
    os,
    model: model || ua.substring(0, 50) + "...",
    deviceType,
    isSmartTV,
    isAndroid,
    isWebView,
    isWebApp,
    domain: window.location.hostname,
    origin: window.location.origin,
    path: window.location.pathname,
    userAgent: ua,
    vendor,
    platform,
    screen: screenInfo,
    hardwareConcurrency,
    deviceMemory: memory,
    maxTouchPoints,
    language: navigator.language,
    languages: navigator.languages?.join(",") || "",
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || "",
    referrer: document.referrer,
    visibilityState: document.visibilityState,
  };
}

async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto !== "undefined" && crypto.subtle?.digest) {
    try {
      await crypto.subtle.digest("SHA-256", new Uint8Array([1, 2, 3]));

      let encodedData: BufferSource;
      if (typeof TextEncoder !== "undefined") {
        encodedData = new TextEncoder().encode(input);
      } else {
        const utf8 = unescape(encodeURIComponent(input));
        const buffer = new Uint8Array(utf8.length);
        for (let i = 0; i < utf8.length; i++) {
          buffer[i] = utf8.charCodeAt(i);
        }
        encodedData = buffer;
      }

      const hashBuffer = await crypto.subtle.digest("SHA-256", encodedData);
      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } catch {
      console.warn(
        "[StormcloudVideoPlayer] crypto.subtle not supported, using fallback hash"
      );
    }
  }

  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const fallbackHash = Math.abs(hash).toString(16).padStart(8, "0");
  const timestamp = Date.now().toString(16).padStart(12, "0");
  const random = Math.random().toString(16).substring(2, 14).padStart(12, "0");

  return (fallbackHash + timestamp + random).padEnd(64, "0");
}

function resolvePlayerIdentitySeed(clientInfo: ClientInfo): string {
  const nativeDeviceId = resolveNativeDeviceId();
  if (nativeDeviceId) {
    return `device:${nativeDeviceId}`;
  }

  const persistedId = getOrCreateStoredUuid(PLAYER_ID_STORAGE_KEY);
  if (persistedId) {
    return `persisted:${persistedId}`;
  }

  return `fingerprint:${JSON.stringify(clientInfo)}`;
}

export async function getBrowserID(clientInfo: ClientInfo): Promise<string> {
  if (cachedBrowserId) {
    return cachedBrowserId;
  }

  cachedBrowserId = await sha256Hex(resolvePlayerIdentitySeed(clientInfo));
  return cachedBrowserId;
}

type PlayerMetricFlags = {
  adLoaded?: boolean;
  adDetect?: boolean;
  captureAt?: string;
};

function canPublish(licenseKey: string | undefined): licenseKey is string {
  return Boolean(isMQTTEnabled() && licenseKey);
}

async function buildPlayerMetricEvent(
  context: PlayerAnalyticsContext = {},
  flags: PlayerMetricFlags = {}
): Promise<Record<string, unknown>> {
  const clientInfo = getClientInfo();

  const debugAdTiming = !!context.debugAdTiming;
  if (debugAdTiming) {
    console.log("[StormcloudVideoPlayer] clientInfo: ", JSON.stringify(clientInfo, null, 2));
  }

  const playerId = await getBrowserID(clientInfo);

  if (debugAdTiming) {
    console.log("[StormcloudVideoPlayer] playerId: ", playerId);
  }

  const captureAt = flags.captureAt ?? new Date().toISOString();

  return {
    player_id: playerId,
    device_type: clientInfo.deviceType,
    os: clientInfo.os.toLowerCase(),
    ad_loaded: flags.adLoaded ?? false,
    ad_detect: flags.adDetect ?? false,
    capture_at: captureAt,
    ...(context.inputStreamType
      ? { input_stream_type: context.inputStreamType }
      : {}),
  };
}

function publishTracking(
  licenseKey: string,
  channel: "metrics" | "impressions" | "heartbeat",
  body: Record<string, unknown>
): void {
  ensureMQTTClient();
  publishMQTT(buildPlayerTopic(licenseKey, channel), body);
}

export async function sendInitialTracking(
  licenseKey?: string,
  context: PlayerAnalyticsContext = {}
): Promise<void> {
  if (!canPublish(licenseKey)) return;

  try {
    const metricEvent = await buildPlayerMetricEvent(context, {
      adLoaded: false,
      adDetect: false,
    });

    publishTracking(licenseKey, "metrics", {
      events: [metricEvent],
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending initial tracking data:",
      error
    );
  }
}

export async function sendAdDetectTracking(
  licenseKey: string | undefined,
  adDetectInfo: AdDetectInfo,
  context: PlayerAnalyticsContext = {}
): Promise<void> {
  try {
    await sendHeartbeat(licenseKey, context, {
      adDetect: true,
      captureAt: adDetectInfo.timestamp,
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending ad detect tracking:",
      error
    );
  }
}

export async function sendAdLoadedTracking(
  licenseKey: string | undefined,
  adLoadedInfo: AdLoadedInfo,
  context: PlayerAnalyticsContext = {}
): Promise<void> {
  try {
    await sendHeartbeat(licenseKey, context, {
      adLoaded: true,
      captureAt: adLoadedInfo.timestamp,
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending ad loaded tracking:",
      error
    );
  }
}

export async function sendAdImpressionTracking(
  licenseKey: string | undefined,
  adImpressionInfo: AdImpressionInfo,
  context: PlayerAnalyticsContext = {}
): Promise<void> {
  if (!canPublish(licenseKey)) return;

  try {
    const metricEvent = await buildPlayerMetricEvent(context, {
      captureAt: adImpressionInfo.timestamp,
    });

    publishTracking(licenseKey, "heartbeat", metricEvent);
    publishTracking(licenseKey, "impressions", {
      events: [
        {
          player_id: metricEvent.player_id,
          ad_played_count: 1,
          ad_url: adImpressionInfo.adUrl,
          capture_at: adImpressionInfo.timestamp,
        },
      ],
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending ad impression tracking:",
      error
    );
  }
}

export async function sendHeartbeat(
  licenseKey?: string,
  context: PlayerAnalyticsContext = {},
  flags: PlayerMetricFlags = {}
): Promise<void> {
  if (!canPublish(licenseKey)) return;

  try {
    const heartbeatData = await buildPlayerMetricEvent(context, flags);
    publishTracking(licenseKey, "heartbeat", heartbeatData);
  } catch (error) {
    console.error("[StormcloudVideoPlayer] Error sending heartbeat:", error);
  }
}
